import React, { useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, ColorPicker, Form, Input, Modal, Tooltip } from 'antd';
import { usePatchSeverityMutation } from 'src/redux/services/centralApi';
import type { Severity } from 'src/interfaces/entities/severity';
import { EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';

interface IEditSeverityForm extends Pick<Severity, 'name' | 'is_active'> {
	notices: INotices[]
}

type INotices = 'in_dashboard' | 'in_telegram' | 'in_webhook' | 'in_zapi';

export default function EditSeverity(
	{
		row,
	}: { row: Severity }
) {
	const [ color, setColor, ] = useState<string>(row.color);
	const [ open, setOpen, ] = useState<boolean>(false);

	const [ patchSeverity, patchSeverityResult, ] = usePatchSeverityMutation();

	const [ editSeverityForm, ] = Form.useForm<IEditSeverityForm>();

	const disabled = useMemo(() => {
		return row.org_id === null;
	}, [ row, ]);

	useEffect(() => {
		editSeverityForm.resetFields();
	}, [ open, row, ]);

	const onFinishEditSeverityForm = ({
		is_active,
		name,
		notices,
	}: IEditSeverityForm) => {
		const _notices: Record<INotices, boolean> = {
			in_dashboard: false,
			in_telegram: false,
			in_webhook: false,
			in_zapi: false,
		};
		notices.forEach(key => {
			_notices[key] = true;
		});

		const _severity: Omit<Severity, 'severity_id' | 'client_id' | 'org_id' | 'creation_date'> = {
			name,
			color,
			is_active,
			..._notices,
		};

		patchSeverity({
			id: row.severity_id,
			severity: _severity,
		})
			.unwrap()
			.then(() => {
				toast.success('Severidade atualizada com sucesso!', {
					autoClose: 1000,
				});
			})
			.catch((err) => {
				if (err?.status && err?.status === 500
					&& err?.data?.frontend_message) {
					toast.error(err?.data?.frontend_message, {
						autoClose: 1000,
					});
				}
			})
			.finally(() => {
				setOpen(false);
				editSeverityForm.resetFields();
			});
	};

	const getInitialNotices = (): string[] => {
		return Object.keys(row)
			.flatMap((key) => {
				if(!key.includes('in')) return [];

				if(row[key as keyof Severity]){
					return key;
				}

				return [];
			});
	};

	return <>
		<Tooltip
			title={
				disabled && 'Severidades padrões não podems ser editadas.'
			}
		>
			<Button
				type="text"
				disabled={disabled}
				icon={<EditOutlined />}
				onClick={() => setOpen(true)}
			/>
		</Tooltip>
		<Modal
			centered
			title={'Editar Severidade'}
			open={open}
			onCancel={() => {
				setOpen(false);
				toast.info('Operação cancelada!', { autoClose: 1000, });
			}}
			destroyOnHidden
			confirmLoading={patchSeverityResult.isLoading}
			okText="Salvar"
			onOk={() => {
				editSeverityForm.submit();
			}}
		>
			<Form
				layout="vertical"
				onFinish={onFinishEditSeverityForm}
				form={editSeverityForm}
				style={{ marginTop: 10, }}
			>
				<Form.Item
					initialValue={row.name}
					name={'name'}
					hasFeedback
					required
					tooltip={'Insira o nome da severidade.'}
					label={'Nome da severidade'}
					rules={[
						{
							required: true,
							message: 'Campo obrigatório.',
						},
					]}
				>
					<Input
						placeholder="Nome da severidade"
					/>
				</Form.Item>
				<Form.Item
					initialValue={row.color}
					hasFeedback
					required
					tooltip={'Insira a cor da severidade.'}
					label={'Cor'}
					rules={[
						{
							required: true,
							message: 'Campo obrigatório.',
						},
					]}
				>
					<ColorPicker
						style={{ width: '100%', }}
						onChange={(_, hex) => setColor(hex)}
					>
						<Button
							type='primary'
							style={{
								backgroundColor: color,
								width: '100%',
							}}
						>
							{color}
						</Button>
					</ColorPicker>
				</Form.Item>
				<Form.Item
					name='notices'
					required
					tooltip={'Selecione por onde será notificado'}
					hasFeedback
					label="Notificações"
					initialValue={getInitialNotices()}
				>
					<Checkbox.Group
						options={[
							{ label: 'Whatsapp', value: 'in_zapi', },
							{ label: 'Telegram', value: 'in_telegram', },
							{ label: 'Dashboard', value: 'in_dashboard', },
							{ label: 'Webhook', value: 'in_webhook', },
						]}
					/>
				</Form.Item>
				<hr />
				<Form.Item
					name='is_active'
					valuePropName={'checked'}
					required
					hasFeedback
					initialValue={row.is_active}
				>
					<Checkbox>Severidade ativa</Checkbox>
				</Form.Item>
			</Form>
		</Modal>
	</>;
}